import React, { useEffect, useState } from "react";
import { authApi, formatErr } from "./api";
import { Breadcrumbs } from "./AdminLayout";
import { Monitor, Smartphone, Tablet, ShieldOff, ShieldCheck } from "lucide-react";
import { toast, Toaster } from "sonner";

const iconFor = (device) => device === "Mobile" ? Smartphone : device === "Tablet" ? Tablet : Monitor;

export default function AdminSessions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = () => authApi.sessions().then(setRows).catch(()=>{});
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this session? The device will be signed out on next request.")) return;
    try { await authApi.revokeSession(id); toast.success("Session revoked"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const revokeAllOthers = async () => {
    if (!window.confirm("Revoke every session except this one?")) return;
    setLoading(true);
    try {
      const r = await authApi.revokeOthers();
      toast.success(`${r.revoked_count} sessions revoked`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-sessions">
      <Toaster richColors position="top-right"/>
      <Breadcrumbs items={["Admin", "Sessions & Devices"]}/>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-navy tracking-tight">Sessions & Devices</h1>
          <p className="text-stone2 text-sm mt-1">Every device signed in to your account. Revoke any that isn't yours.</p>
        </div>
        <button data-testid="revoke-others-btn" onClick={revokeAllOthers} disabled={loading}
          className="inline-flex items-center gap-2 border border-[#9F1239] text-[#9F1239] hover:bg-[#9F1239] hover:text-white text-sm px-4 py-2 disabled:opacity-60">
          <ShieldOff size={14} strokeWidth={1.5}/> Revoke all others
        </button>
      </div>

      <div className="bg-white border border-line divide-y divide-line">
        {rows.map(r => {
          const Icon = iconFor(r.device);
          return (
            <div key={r.id} data-testid={`session-${r.id}`} className="p-5 flex items-center gap-5">
              <div className={`w-11 h-11 flex items-center justify-center ${r.is_active ? "bg-clinical text-navy" : "bg-line text-stone2"}`}>
                <Icon size={20} strokeWidth={1.5}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-serif text-lg text-navy">{r.browser} on {r.os}</div>
                  {r.is_current && <span className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 bg-prestige-bg text-prestige rounded-full flex items-center gap-1"><ShieldCheck size={11}/> This device</span>}
                  {!r.is_active && <span className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 bg-line text-stone2 rounded-full">Revoked</span>}
                </div>
                <div className="mono text-xs text-stone2 mt-1 truncate">
                  IP {r.ip || "—"} · Signed in {r.created_at?.slice(0,16).replace('T',' ')} · Last seen {r.last_seen_at?.slice(0,16).replace('T',' ')}
                </div>
              </div>
              {r.is_active && !r.is_current && (
                <button data-testid={`revoke-${r.id}`} onClick={()=>revoke(r.id)} className="mono text-[10px] uppercase tracking-widest text-[#9F1239] hover:underline">Revoke</button>
              )}
            </div>
          );
        })}
        {rows.length === 0 && <div className="p-6 text-stone2">No sessions.</div>}
      </div>
    </div>
  );
}
